import {Album} from '@app/web-player/albums/album';
import {Track} from '@app/web-player/tracks/track';
import {
  MediaViewAccessPermissions,
  MediaPlayAccessPermissions,
  TrackViewAccessOptions,
  TrackPlayAccessOptions,
  canViewTrack,
  canPlayTrack,
} from '@app/web-player/tracks/utils/track-filter';

/**
 * Représente un Album enrichi de propriétés utiles pour l'affichage UI ou le filtrage.
 */
export interface EnhancedAlbum extends Album {
  isReleased: boolean;
  isAvailable: boolean;
  isEncoded: boolean;
  isExclusivity: boolean;
  releaseDate?: Date;
}

/**
 * Options de filtrage pour la visualisation d’un album.
 * Permet d’activer ou non les critères (released, available, encoded) à appliquer,
 * tout en tenant compte des permissions d’accès correspondantes.
 */
export interface AlbumViewAccessOptions {
  permissions: MediaViewAccessPermissions;
  userId?: number;
  useReleased?: boolean;
  useAvailable?: boolean;
  useEncoded?: boolean;
}

/**
 * Options de filtrage pour la lecture d’un album.
 * Fonctionne comme AlbumViewAccessOptions mais pour les droits de lecture.
 */
export interface AlbumPlayAccessOptions {
  permissions: MediaPlayAccessPermissions;
  userId?: number;
  useReleased?: boolean;
  useAvailable?: boolean;
  useEncoded?: boolean;
}

/**
 * Crée une fonction de filtre à appliquer sur un tableau d’albums pour la visualisation.
 */
export function createAlbumViewFilter(options: AlbumViewAccessOptions) {
  const {
    userId,
    useReleased = false,
    useAvailable = true,
    useEncoded = true
  } = options;
  const permissions = normalizePermissions(options.permissions, {
      unreleased: false,
      unavailable: false,
      unencoded: false
  });

  const now = new Date();

  return (album: Album): boolean => {
    if (userId && album.owner_id === userId) return true;

    const releaseDate = album.k_release_date ?? album.release_date;
    const isReleased = releaseDate ? new Date(releaseDate) <= now : false;
    const isAvailable = 'available' in album ? !!album.available : true;
    const isEncoded = 'encoded' in album ? !!album.encoded : true;

    return (
      (!useReleased || permissions.unreleased || isReleased) &&
      (!useAvailable || permissions.unavailable || isAvailable) &&
      (!useEncoded || permissions.unencoded || isEncoded)
    );
  };
}

/**
 * Crée une fonction de filtre à appliquer sur un tableau d’albums pour la lecture.
 */
export function createAlbumPlayFilter(options: AlbumPlayAccessOptions) {
  const {
    userId,
    useReleased = true,
    useAvailable = true,
    useEncoded = true
  } = options;
  const permissions = normalizePermissions(options.permissions, {
      unreleased: false,
      unavailable: false,
      unencoded: false
  });

  const now = new Date();

  return (album: Album): boolean => {
    if (userId && album.owner_id === userId) return true;

    const releaseDate = album.k_release_date ?? album.release_date;
    const isReleased = releaseDate ? new Date(releaseDate) <= now : false;
    const isAvailable = 'available' in album ? !!album.available : true;
    const isEncoded = 'encoded' in album ? !!album.encoded : true;

    return (
      (!useReleased || permissions.unreleased || isReleased) &&
      (!useAvailable || permissions.unavailable || isAvailable) &&
      (!useEncoded || permissions.unencoded || isEncoded)
    );
  };
}

/**
 * Vérifie si un album est visible selon les permissions et les critères activés.
 */
export function canViewAlbum(album: Album, options: AlbumViewAccessOptions): boolean {
  if (options.userId && album.owner_id === options.userId) return true;
  const {
    useReleased = false,
    useAvailable = true,
    useEncoded = true
  } = options;
  const permissions = normalizePermissions(options.permissions, {
      unreleased: false,
      unavailable: false,
      unencoded: false
  });
  
  const now = new Date();
  const releaseDate = album.k_release_date ?? album.release_date;
  const isReleased = releaseDate ? new Date(releaseDate) <= now : false;
  const isAvailable = 'available' in album ? !!album.available : true;
  const isEncoded = 'encoded' in album ? !!album.encoded : true;

  return (
    (!useReleased || permissions.unreleased || isReleased) &&
    (!useAvailable || permissions.unavailable || isAvailable) &&
    (!useEncoded || permissions.unencoded || isEncoded)
  );
}

/**
 * Vérifie si un album est lisible selon les permissions de lecture et les critères activés.
 */
export function canPlayAlbum(album: Album, options: AlbumPlayAccessOptions): boolean {
  if (options.userId && album.owner_id === options.userId) return true;
  const {
    useReleased = true,
    useAvailable = true,
    useEncoded = true
  } = options;
  const permissions = normalizePermissions(options.permissions, {
      unreleased: false,
      unavailable: false,
      unencoded: false
  });

  const now = new Date();
  const releaseDate = album.k_release_date ?? album.release_date;
  const isReleased = releaseDate ? new Date(releaseDate) <= now : false;
  const isAvailable = 'available' in album ? !!album.available : true;
  const isEncoded = 'encoded' in album ? !!album.encoded : true;

  return (
    (!useReleased || permissions.unreleased || isReleased) &&
    (!useAvailable || permissions.unavailable || isAvailable) &&
    (!useEncoded || permissions.unencoded || isEncoded)
  );
}

/**
 * Vérifie si un album contient au moins un track visible selon les options utilisateur.
 */
export function albumHasPublicTrack(
  album: Album & { tracks?: Track[] },
  options: TrackViewAccessOptions
): boolean {
  if (!album.tracks?.length) return false;
  return album.tracks.some(track => canViewTrack(track, options));
}

/**
 * Vérifie si un album contient au moins un track lisible selon les options utilisateur.
 */
export function albumHasPlayableTrack(
  album: Album & { tracks?: Track[] },
  options: TrackPlayAccessOptions
): boolean {
  if (!album.tracks?.length) return false;
  return album.tracks.some(track => canPlayTrack(track, options));
}

/**
 * Enrichit un album avec des flags utiles pour l'affichage : isReleased, isAvailable, isEncoded, isExclusivity.
 */
export function addAlbumFlags<T extends Album>(album: T): T & EnhancedAlbum {
  const now = new Date();

  const kDate = album.k_release_date ? new Date(album.k_release_date) : undefined;
  const publicDate = album.release_date ? new Date(album.release_date) : undefined;

  const releaseDate = kDate ?? publicDate;
  const isReleased = releaseDate ? releaseDate <= now : false;
  const isAvailable = 'available' in album ? !!album.available : true;
  const isEncoded = 'encoded' in album ? !!album.encoded : true;

  const isExclusivity =
    album.exclusivity ||
    kDate && publicDate && kDate < publicDate && now < publicDate;

  return {
    ...album,
    isReleased,
    isAvailable,
    isEncoded,
    isExclusivity: !!isExclusivity,
    releaseDate
  };
}

function normalizePermissions<T extends Record<string, any>>(
  source: Partial<T> | undefined,
  defaults: T
): T {
  return {
    ...defaults,
    ...Object.fromEntries(
      Object.entries(defaults).map(([key, def]) => [key, source?.[key] ?? def])
    ),
  };
}